// Streaming client for Ollama's /api/generate endpoint.
//
// Why streaming: Cloudflare's 100s time-to-first-byte limit (HTTP 524) kills
// any non-streaming inference where the origin holds the full response in
// memory before sending it. With `stream: true`, Ollama emits tokens as JSON
// chunks as soon as they're generated, so data is continuously flowing
// through the connection from the first token onward — Cloudflare stays
// happy regardless of total generation time.
//
// This helper reads the streaming body, concatenates every `response` field
// across newline-delimited JSON chunks, and returns the accumulated string
// once `done` is true. Works identically with `format: 'json'` — the
// concatenated chunks form one valid JSON object at the end.
//
// Usage:
//   const raw = await streamOllamaGenerate({
//     serverUrl: process.env.REACT_APP_OLLAMA_URI,
//     apiKey:    process.env.REACT_APP_OLLAMA_KEY,
//     body:      { model, prompt, format: 'json', options: { temperature: 0.1 } },
//     onProgress: (acc) => setMsg(`${acc.length} chars streamed`)
//   })
//   const parsed = JSON.parse(raw)
const THINKS = /^(qwen3|gemma4)/

export async function streamOllamaGenerate({ serverUrl, body, apiKey, onProgress }) {
  const res = await fetch(`${serverUrl}/api/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey || ''
    },
    // Newer Qwen and Gemma models can think before answering — slower, and the
    // reasoning mustn't reach a JSON parser — so it's switched off.
    body: JSON.stringify({ ...body, stream: true, keep_alive: '30m', ...(THINKS.test(body.model || '') ? { think: false } : {}) })
  })
  if (!res.ok) throw new Error(`Ollama HTTP Error: ${res.status}`)
  if (!res.body) throw new Error('Ollama returned no response body to stream')

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let accumulated = ''

  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? '' // hold incomplete tail line for next chunk
    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed) continue
      try {
        const obj = JSON.parse(trimmed)
        if (obj.error) throw new Error(`Ollama error: ${obj.error}`)
        if (typeof obj.response === 'string') {
          accumulated += obj.response
          if (onProgress) onProgress(accumulated, obj)
        }
      } catch (e) {
        if (e.message?.startsWith('Ollama error')) throw e
        // Otherwise it's a partial line; the next read completes it.
      }
    }
  }
  // Flush trailing buffer if Ollama omitted a final newline
  if (buffer.trim()) {
    try {
      const obj = JSON.parse(buffer.trim())
      if (typeof obj.response === 'string') accumulated += obj.response
    } catch (e) { /* ignore */ }
  }
  return accumulated
}
